import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  setDoc
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import EditRosterModal from "../components/EditRosterModal";
import PageHeader from "../components/PageHeader";
import ArchivePersonCard from "../components/ArchivePersonCard";

function RostersOverviewPage() {
  const [rosters, setRosters] = useState([]);
  const [editingRoster, setEditingRoster] = useState(null);
  const navigate = useNavigate();
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [archivedPeople, setArchivedPeople] = useState([]);
  const [selectedRosterId, setSelectedRosterId] = useState(""); // Add this state



  useEffect(() => {
    const fetchRosters = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) {
          console.error("User not authenticated");
          return;
        }
  
        // Fetch rosters for the authenticated user
        const rostersRef = collection(db, "users", userId, "rosters");
        const querySnapshot = await getDocs(rostersRef);
  
        // Map rosters and include previewFields
        const userRosters = await Promise.all(
          querySnapshot.docs.map(async (doc) => {
            const rosterData = { id: doc.id, ...doc.data() };
  
            // Fetch people within the roster
            const peopleRef = collection(db, "users", userId, "rosters", doc.id, "people");
            const peopleSnapshot = await getDocs(peopleRef);
  
            const people = peopleSnapshot.docs.map((personDoc) => ({
              id: personDoc.id,
              ...personDoc.data(),
            }));
  
            // Return roster data with people and previewFields
            return {
              ...rosterData,
              people,
              previewFields: rosterData.previewFields || [],
            };
          })
        );
  
        setRosters(userRosters);
        console.log("Fetched rosters with people and previewFields:", userRosters);
      } catch (error) {
        console.error("Error fetching rosters:", error);
      }
    };
  
    fetchRosters();
  }, []);
  
  useEffect(() => {
    if (isArchiveModalOpen) {
      fetchArchivedPeople();
    }
  }, [isArchiveModalOpen]);

  const addRoster = async (newRoster) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        console.error("User not authenticated");
        return;
      }

      const rostersRef = collection(db, "users", userId, "rosters");
      const docRef = await addDoc(rostersRef, {
        name: newRoster.name || "Untitled Roster",
        customFields: newRoster.customFields || [],
      });

      setRosters((prev) => [
        ...prev,
        {
          id: docRef.id,
          name: newRoster.name,
          customFields: newRoster.customFields || [],
        },
      ]);
      console.log("Roster added:", newRoster);
    } catch (error) {
      console.error("Error adding roster:", error);
    }
  };

  const saveRoster = async (updatedRoster) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        console.error("User not authenticated");
        return;
      }

      const rosterRef = doc(db, "users", userId, "rosters", updatedRoster.id);
      await updateDoc(rosterRef, {
        name: updatedRoster.name,
        customFields: updatedRoster.customFields,
        previewFields: updatedRoster.previewFields,
        previewTags: updatedRoster.previewTags,
      });

      setRosters((prev) =>
        prev.map((roster) =>
          roster.id === updatedRoster.id ? updatedRoster : roster
        )
      );
      console.log("Roster updated:", updatedRoster);
    } catch (error) {
      console.error("Error updating roster:", error);
    }
  };

  const deleteRoster = async (id) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        console.error("User not authenticated");
        return;
      }

      const rosterRef = doc(db, "users", userId, "rosters", id);
      await deleteDoc(rosterRef);

      setRosters((prev) => prev.filter((roster) => roster.id !== id));
      console.log("Roster deleted:", id);
    } catch (error) {
      console.error("Error deleting roster:", error);
    }
  };

  const fetchArchivedPeople = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error("User not authenticated");
      }
  
      const archiveRef = collection(db, "users", userId, "archivedPeople");
      const snapshot = await getDocs(archiveRef);
      const people = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setArchivedPeople(people);
    } catch (error) {
      console.error("Error fetching archived people:", error);
    }
  };

  const handleUnarchive = async (person) => {
    if (!selectedRosterId) {
      alert("Please select a roster to unarchive this person.");
      return;
    }
  
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error("User not authenticated");
      }
  
      const archiveRef = doc(db, "users", userId, "archivedPeople", person.id);
  
      // Check if the person already exists in the roster
      const peopleRef = collection(db, "users", userId, "rosters", selectedRosterId, "people");
      const existingDocSnap = await getDoc(doc(peopleRef, person.id));
      if (existingDocSnap.exists()) {
        throw new Error(`Person with ID ${person.id} already exists in the roster.`);
      }
  
      // Add the person back to the selected roster
      const updatedPerson = { ...person, rosterId: selectedRosterId }; // Update the rosterId
      await setDoc(doc(peopleRef, person.id), updatedPerson);
  
      // Remove the person from the archivedPeople collection
      await deleteDoc(archiveRef);
  
      // Update the UI
      setArchivedPeople((prev) => prev.filter((p) => p.id !== person.id));
      console.log("Person successfully unarchived:", person.id);
    } catch (error) {
      console.error("Error unarchiving person:", error);
      alert("Failed to unarchive person. Please try again.");
    }
  };
  
  

  return (
    <>
      <PageHeader title="Rosters Test" />
      <div className="container">
        <div className="actions">
        <button
        aria-label="View Archive"
        onClick={() => setIsArchiveModalOpen(true)}
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          backgroundColor: "#f5f5f5",
          color: "#000",
          border: "1px solid #ddd",
          borderRadius: "8px",
          width: "40px",
          height: "40px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          style={{
            width: "24px",
            height: "24px",
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 16l4-4m-4 4l-4-4m4 4V4m-7 12h14"
          />
        </svg>
      </button>

        <button
          aria-label="Add Roster"
          onClick={() => setEditingRoster({ name: "", customFields: [] })}
          style={{
            backgroundColor: "black",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "30px",
            height: "30px",
            fontSize: "24px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          +
        </button>

        </div>
        <ul className="roster-list">
          {rosters.map((roster) => (
            <li key={roster.id} className="roster-item">
              <span
                className="roster-name"
                onClick={() => navigate(`/roster/${roster.id}`)}
              >
                {roster.name}
              </span>
              <button
                className="edit-btn"
                onClick={() => setEditingRoster(roster)}
              >
                Edit
              </button>
            </li>
          ))}
        </ul>
        {editingRoster && (
          <EditRosterModal
            roster={editingRoster}
            onSave={(updatedRoster) => {
              if (updatedRoster.id) {
                saveRoster(updatedRoster);
              } else {
                addRoster(updatedRoster);
              }
            }}
            onDelete={deleteRoster}
            onClose={() => setEditingRoster(null)}
          />
        )}
      </div>
      {isArchiveModalOpen && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    }}
    onClick={() => setIsArchiveModalOpen(false)}
  >
    <div
      style={{
        backgroundColor: "#fff",
        padding: "20px",
        borderRadius: "8px",
        width: "400px",
        textAlign: "center",
        position: "relative",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <h2>Archived People</h2>
      <ul style={{ listStyleType: "none", padding: 0 }}>
        {archivedPeople.length === 0 ? (
          <p>No archived people.</p>
        ) : (
          archivedPeople.map((person) => (
            <li
              key={person.id}
              style={{
                padding: "10px",
                borderBottom: "1px solid #ddd",
                textAlign: "left",
              }}
            >
              {person.name} ({person.age})
              <button
                onClick={() => handleUnarchive(person)}
                style={{
                  marginLeft: "10px",
                  color: "white",
                  backgroundColor: "green",
                  borderRadius: "5px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Unarchive
              </button>
            </li>
          ))
        )}
      </ul>
      <button
        onClick={() => setIsArchiveModalOpen(false)}
        style={{
          marginTop: "10px",
          padding: "10px",
          backgroundColor: "black",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        Close
      </button>
    </div>
  </div>
)}
{isArchiveModalOpen && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    }}
    onClick={() => setIsArchiveModalOpen(false)}
  >
    <div
      style={{
        backgroundColor: "#fff",
        padding: "20px",
        borderRadius: "8px",
        width: "600px",
        height: "80vh",
        overflowY: "auto",
        position: "relative",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <h2 style={{ textAlign: "center" }}>Archived People</h2>
      {archivedPeople.length === 0 ? (
        <p style={{ textAlign: "center" }}>No archived people.</p>
      ) : (
        archivedPeople.map((person) => (
<ArchivePersonCard
  key={person.id}
  person={person}
  rosters={rosters} // Pass available rosters
  selectedRosterId={selectedRosterId} // Pass selected roster ID
  setSelectedRosterId={setSelectedRosterId} // Pass state updater
  onUnarchive={handleUnarchive}
/>
        ))
      )}
      <button
        onClick={() => setIsArchiveModalOpen(false)}
        style={{
          marginTop: "20px",
          padding: "10px",
          backgroundColor: "black",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          width: "100%",
        }}
      >
        Close
      </button>
    </div>
  </div>
)}

    </>
  );
}

export default RostersOverviewPage;
